import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from 'moment';
import { i18n } from '../../../translate/i18n';
import { isEmpty } from 'lodash';
import * as actions from "../../../store/actions";

import {
  Paper, Avatar, Container, Table, TableBody, TableCell, TableFooter, TablePagination, TableRow, Typography, Chip, Button, Drawer,
} from '@mui/material';

import FilterList from '@mui/icons-material/FilterList';
import Spinner from '../../../components/Spinner/index';
import EnhancedTableHead from "../../../UI/MaterialTableOverrides/EnhancedTableHead";
import TablePaginationActionsWrapped from "../../../UI/MaterialTableOverrides/TablePaginationActions";
import TableTitle from '../../../UI/MaterialTableOverrides/TableTitle';
import { formatLanguageFromLocalStorage } from '../../../utils/formatLanguageFromLocalStorage';
import ExpiredServicesFilterModal from "../ModalFilter/ExpiredServices";

export default function TableExpiredServices(props) {
  const dispatch = useDispatch();
  const textInput = useRef(null);

  const [page, setPage] = useState(1);
  const [alreadySerched, setAlreadySearched] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  const [limit, setLimit] = useState(5);
  const [searchText, setSearchText] = useState("");
  const [orderBy, setOrderBy] = useState("equipmentServicesReplicate.expirationDate");
  const [order, setOrder] = useState("desc");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const selectedSites = useSelector(state => state.sites.selectedSitesPlaces);
  // Modal Filter
  const filteredPlaces = useSelector(state => state.expiredServices.filteredPlaces);
  const filteredAreas = useSelector(state => state.expiredServices.filteredAreas);
  const filteredEquipmentModels = useSelector(state => state.expiredServices.filteredEquipmentModels);
  const filteredEquipmentGroups = useSelector(state => state.expiredServices.filteredEquipmentGroups);
  const filteredCompanies = useSelector(state => state.expiredServices.filteredCompanies);
  const filteredServiceTypes = useSelector(state => state.expiredServices.filteredServiceTypes);
  const filteredFrequencies = useSelector(state => state.expiredServices.filteredFrequencies);
  const after = useSelector(state => state.expiredServices.after);
  const before = useSelector(state => state.expiredServices.before);
  const showMandatoryServices = useSelector(state => state.expiredServices.filterShowMandatoryServices);
  const showNonMandatoryServices = useSelector(state => state.expiredServices.filterShowNonMandatoryServices);

  const fetchExpiredServices = useCallback(() => dispatch(actions.fetchExpiredServices(page, limit, searchText, order, orderBy, after, before, selectedSites, filteredEquipmentModels, filteredEquipmentGroups, filteredAreas, filteredCompanies, filteredPlaces, filteredServiceTypes, filteredFrequencies, showMandatoryServices, showNonMandatoryServices)),
  [page, limit, searchText, order, orderBy, selectedSites, filteredPlaces, filteredEquipmentModels, filteredEquipmentGroups, filteredServiceTypes, filteredCompanies, filteredFrequencies, after, before, showMandatoryServices, showNonMandatoryServices, filteredAreas]
  );
  
  const localStorageLang = localStorage.getItem("i18nextLng");
  const emailLanguage = formatLanguageFromLocalStorage(localStorageLang);
  
  // Export Excell
  const fetchExpiredServicesExport = useCallback(() => dispatch(actions.fetchExpiredServicesExport(searchText, order, orderBy, after, before, selectedSites, filteredEquipmentModels, filteredEquipmentGroups, filteredAreas, filteredCompanies, filteredPlaces, filteredServiceTypes, filteredFrequencies, showMandatoryServices, showNonMandatoryServices, emailLanguage)),
    [searchText, order, orderBy, after, before, selectedSites, filteredEquipmentModels, filteredEquipmentGroups, filteredAreas, filteredCompanies, filteredPlaces, filteredServiceTypes, filteredFrequencies, emailLanguage, showMandatoryServices, showNonMandatoryServices]
  );

  const fetchExpiredServicesBadge = useCallback(() => dispatch(actions.fetchExpiredServicesBadge()), []);
  
  const services = useSelector(state => state.expiredServices.expiredServicesEvents);
  const servicesFilter = useSelector(state => state.expiredServices.expiredServicesFilter);
  const loading = useSelector(state => state.expiredServices.loading);

  const headRows = [
    { id: "equipmentServicesReplicate.expirationDate", numeric: false, disablePadding: false, label: i18n.t('headRowsExpiredServices.date') },
    { id: "equipmentModel.name", numeric: false, disablePadding: false, label: i18n.t('headRowsExpiredServices.equipmentEquipmentParamName') },
    { id: "equipmentServicesReplicate.equipmentServiceId.serviceType.name", numeric: false, disablePadding: false, label: i18n.t('headRowsExpiredServices.inspection') },
    { id: "place.path", numeric: false, disablePadding: false, label: i18n.t('headRowsExpiredServices.placeName') },
  ];

	useEffect(() => {
		dispatch(actions.setOpenDrawer(true))
	}, [])

	useEffect(() => {
    fetchExpiredServicesBadge()
	}, [])

  useEffect(() => {
    if (!isEmpty(servicesFilter)) {
      setPage(servicesFilter.page);
      setLimit(servicesFilter.limit);
      setSearchText(servicesFilter.searchText);
      setOrderBy(servicesFilter.orderBy);
      setOrder(servicesFilter.order);
    }

    setFirstLoad(false);
  }, []);

  useEffect(() => {
    if (!firstLoad) {
      fetchExpiredServices(page, limit, searchText, order, orderBy, after, before, selectedSites, filteredEquipmentModels, filteredEquipmentGroups, filteredAreas, filteredCompanies, filteredPlaces, filteredServiceTypes, filteredFrequencies);
      setAlreadySearched(true);
    }
  }, [firstLoad, page, limit, order, orderBy])

  useEffect(() => {
    if (!firstLoad) {
      setPage(1);
      fetchExpiredServices(page, limit, searchText, order, orderBy,after, before, selectedSites, filteredEquipmentModels, filteredEquipmentGroups, filteredAreas, filteredCompanies, filteredPlaces, filteredServiceTypes, filteredFrequencies);
      setAlreadySearched(true);
    }
  }, [selectedSites, filteredPlaces, filteredAreas, filteredCompanies, filteredEquipmentModels, filteredEquipmentGroups, filteredServiceTypes, filteredFrequencies, after, before, showMandatoryServices, showNonMandatoryServices]);

  useEffect(() => {
    if (searchText !== "" || alreadySerched) {
      const timer = setTimeout(() => {
        if (searchText === textInput.current.value) {
          fetchExpiredServices();
        }
      }, 500);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [searchText, textInput]);

  const handleChangePage = (e, page) => {
    setPage(page + 1);
  };

  const handleChangeRowsPerPage = e => {
    setLimit(e.target.value);
    setPage(1);
  };

  const handleRequestSort = (event, property) => {
    const isDesc = orderBy === property && order === "desc";
    setOrder(isDesc ? "asc" : "desc");
    setOrderBy(property);
    setPage(1)
  };

  const selectRow = (e, id) => {
    props.history.push(`form-equip/${id}/expired-services`)
  }

  const styles = {
    table: {
      position: 'relative',
      marginBottom: 50,
    },
    tableRow: {
      cursor: 'pointer',
    },
    opacityContainer: {
      opacity: loading ? '0.3' : '1',
    },
    spinner: {
      position: 'absolute',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
      top: '-5px',
      opacity: '1',
      zIndex: 2,
    },
    noResults: {
      height: '100px',
    },
    avatar: {
      width: '2.75rem',
      height: '2.75rem',
      marginRight: '0.25rem',
    },
    avatarSmallText: {
      fontSize: '0.75rem',
      width: '2.75rem',
      height: '2.75rem',
      marginRight: '0.25rem',
    },
    cell: {
      fontSize: '0.775rem',
      padding: '0.25rem',
    },
    cellPhotoContainer: {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'row',
    },
    buttonFilterIcon: {
      marginLeft: 5
    },
    buttonFilter: {
      zIndex: 200,
      backgroundColor: 'green',
      height: '2.2rem',
      width: '6.5rem',
      padding: '1rem',
      color: '#fff',
    },
    clearFilter: {
      cursor: 'pointer',
      textDecoration: 'underline',
      zIndex: 200,
      padding: '0.75rem 0.5rem',
      color: '#000',
    },
    filterChipContainer: {
      paddingLeft: 20,
      display: 'flex',
      flexWrap: 'wrap',
    },
    filterChip: {
      cursor: 'pointer',
      margin: 5,
    }
  }

  const deleteFilteredSite = (site) => {
    dispatch(actions.deleteFilteredSite(site))
  }

  const deleteFilteredPlaces = (place) => {
    dispatch(actions.filterExpiredServicesPlacesRemove(place))
  }

  const deleteFilteredAreas = (area) => {
    dispatch(actions.filterExpiredServicesAreasRemove(area))
  }

  const deleteFilteredCompanies = (company) => {
    dispatch(actions.filterExpiredServicesCompaniesRemove(company))
  }

  const deleteFilteredServiceTypes = (serviceType) => {
    dispatch(actions.filterExpiredServiceTypesRemove(serviceType))
  }

  const deleteFilteredFrequency = (frequency) => {
    dispatch(actions.filterExpiredServicesFrequenciesRemove(frequency))
  }

  const deleteFilteredModels = (model) => {
    dispatch(actions.filterExpiredServicesEquipmentModelsRemove(model))
  }

  const deleteFilteredGroups = (group) => {
    dispatch(actions.filterExpiredServicesEquipmentGroupsRemove(group))
  }

  const deleteFilteredStartDate = () => {
    dispatch(actions.filterExpiredServiceStartDateRemove())
  }

  const deleteFilteredEndDate = () => {
    dispatch(actions.filterExpiredServiceEndDateRemove())
  }

  const clearFilterButton = () => {
    if ((filteredServiceTypes && filteredServiceTypes.length > 0)
      || (filteredPlaces && filteredPlaces.length > 0)
      || (filteredAreas && filteredAreas.length > 0)
      || (filteredEquipmentModels && filteredEquipmentModels.length > 0)
      || (filteredEquipmentGroups && filteredEquipmentGroups.length > 0)     
      || (filteredFrequencies && filteredFrequencies.length > 0)
      || (filteredCompanies && filteredCompanies.length > 0)
      || (after)
      || (before)
      || (showNonMandatoryServices)
      || (showMandatoryServices)
    )
      return <Typography onClick={handleClickClearFilters} style={styles.clearFilter}>{i18n.t('btnClearFilterExpiredServices.Typography')}</Typography>;
  };

  const renderSitesChips = () => (selectedSites && selectedSites.map((site) => (<Chip
    key={site._id}
    label={site.name}
    onDelete={() => deleteFilteredSite(site)}
    style={styles.filterChip} />)))
  const renderPlacesChips = () => (filteredPlaces && filteredPlaces.map((place) => (<Chip
    key={place.value}
    label={place.label}
    onDelete={() => deleteFilteredPlaces(place)}
    style={styles.filterChip} />)))
  const renderAreasChips = () => (filteredAreas && filteredAreas.map((area) => (<Chip
    key={area.value}
    label={area.label}
    onDelete={() => deleteFilteredAreas(area)}
    style={styles.filterChip} />)))
  const renderCompaniesChips = () => (filteredCompanies && filteredCompanies.map((company) => (<Chip
    key={company.value}
    label={company.label}
    onDelete={() => deleteFilteredCompanies(company)}
    style={styles.filterChip} />)))
  const renderServiceTypesChips = () => (filteredServiceTypes && filteredServiceTypes.map((serviceType) => (<Chip
    key={serviceType.value}
    label={serviceType.label}
    onDelete={() => deleteFilteredServiceTypes(serviceType)}
    style={styles.filterChip} />)))
  const renderFrequenciesChips = () => (filteredFrequencies && filteredFrequencies.map((frequency) => (<Chip
    key={frequency.value}
    label={frequency.label}
    onDelete={() => deleteFilteredFrequency(frequency)}
    style={styles.filterChip} />)))
  const renderModelsChips = () => (filteredEquipmentModels && filteredEquipmentModels.map((model) => (<Chip
    key={model.value}
    label={model.label}
    onDelete={() => deleteFilteredModels(model)}
    style={styles.filterChip} />)))
  const renderGroupsChips = () => (filteredEquipmentGroups && filteredEquipmentGroups.map((group) => (<Chip
    key={group.value}
    label={group.label}
    onDelete={() => deleteFilteredGroups(group)}
    style={styles.filterChip} />)))
  const renderStartDateChip = () => (after && <Chip
    label={`${i18n.t('chipsExpiredServices.from')} ${moment(after).format('DD/MM/YYYY')}`}
    onDelete={() => deleteFilteredStartDate()}
    style={styles.filterChip} />)
  const renderEndDateChip = () => (before && <Chip
    label={`${i18n.t('chipsExpiredServices.to')} ${moment(before).format('DD/MM/YYYY')}`}
    onDelete={() => deleteFilteredEndDate()}
    style={styles.filterChip} />)

  const renderMandatoryChips = (text, onDelete) => <Chip
    label={text}
    onDelete={() => onDelete()}
    style={styles.filterChip} />

  const handleClickClearFilters = useCallback(() => dispatch(actions.filterExpiredServicesClear()));

  const exportExcel = () => {
    setExportLoading(true);

    fetchExpiredServicesExport()
      .then(response => {
        if (response) {
          window.open(response.url, "_blank")
        }
        setExportLoading(false);
      })
      .catch(() => setExportLoading(false))
  }

  return <>
    <Drawer
      variant="temporary"
      anchor="right"
      onClose={() => setIsFilterOpen(false)}
      open={isFilterOpen}>
      <ExpiredServicesFilterModal
        closeModal={() => setIsFilterOpen(false)}
      />
    </Drawer>
    <Container maxWidth="xl">
      <Paper>
        <TableTitle
          title={i18n.t('tableTitleExpiredServices.title')}
          placeholder={i18n.t('tableTitleExpiredServices.placeholder')}
          inputRef={textInput}
          value={searchText}
          onChange={event => {
            setPage(1);
            setSearchText(event.target.value);
          }}
          clickExport={exportExcel}
          downloading={exportLoading}
          exportButtonDisabled={loading}
        />
        <div style={styles.filterChipContainer}>
          <Button
            variant='contained'
            onClick={() => setIsFilterOpen(true)}
            style={styles.buttonFilter}>
          {i18n.t('btnExpiredServices.label')} <FilterList style={styles.buttonFilterIcon} />
          </Button>
          {renderSitesChips()}
          {renderModelsChips()}
          {renderGroupsChips()}
          {renderPlacesChips()}
          {renderAreasChips()}
          {renderCompaniesChips()}
          {renderServiceTypesChips()}
          {renderFrequenciesChips()}
          {renderStartDateChip()}
          {renderEndDateChip()}
          { showMandatoryServices && renderMandatoryChips(i18n.t('chipsExpiredServices.mandatory'), () => dispatch(actions.expiredServicesSetShowMandatoryServicesFilter(false))) }
          { showNonMandatoryServices && renderMandatoryChips(i18n.t('chipsExpiredServices.nonMandatory'), () => dispatch(actions.expiredServicesSetShowNonMandatoryServicesFilter(false))) }
          {clearFilterButton()}
        </div>
        <Table style={styles.table}>
          <tbody>
            <tr>
              <td>
                {loading &&
                  <div style={styles.spinner}>
                    <Spinner />
                  </div>}
                {services.rows.length === 0 &&
                  <div style={styles.noResults}>
                    {!loading && <Typography style={styles.spinner}>{i18n.t('btnExpiredServices.Typography')}</Typography>}
                  </div>}
              </td>
            </tr>
          </tbody>
          <EnhancedTableHead
            style={styles.opacityContainer}
            headRows={headRows}
            order={order ? order : 'desc'}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={services.rows ? services.rows.length : 0}
          />
          <TableBody style={styles.opacityContainer}>
            {services.rows.map((serv, index) => (
              <TableRow
                style={styles.tableRow}
                key={serv._id+index}
                hover
                onClick={event => selectRow(event, serv._id)}
              >
                <TableCell style={{ ...styles.cell, paddingLeft: '1rem', width: "1%" }}>{moment(serv.equipmentServicesReplicate.expirationDate).format('DD/MM/YYYY')}</TableCell>
                <TableCell style={styles.cell}>
                  <div style={styles.cellPhotoContainer}>
                    {serv?.photo ?
                      <Avatar src={serv?.photo?.url} style={styles.avatar} /> : <Avatar style={styles.avatarSmallText}>{i18n.t('tableCellExpiredServices.photo')}</Avatar>}

                    <div>
                      <div>{serv?.equipmentModel?.name}</div>
                      <div style={{ color: 'grey' }}>{serv.serialNumber}</div>
                    </div>
                  </div>
                </TableCell>
                <TableCell style={styles.cell}>
                  {serv.equipmentServicesReplicate?.equipmentServiceId?.serviceType?.name}{' '}({serv?.equipmentServicesReplicate?.equipmentServiceId?.frequency?.name})
                </TableCell>
                <TableCell style={styles.cell}>{serv?.place?.path}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter style={styles.opacityContainer}>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                count={services.count}
                rowsPerPage={parseInt(limit)}
                page={page - 1}
                SelectProps={{
                  native: true
                }}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActionsWrapped}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </Paper>
    </Container>
  </>;
}
